import { useState, useEffect } from 'react';
import { useAuth, API } from '../../App';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  History, 
  Search, 
  ChevronLeft, 
  ChevronRight, 
  ChevronsLeft, 
  ChevronsRight,
  Calendar,
  MapPin,
  User,
  Phone,
  Clock,
  CheckCircle,
  XCircle,
  Eye,
  Filter,
  X,
  Copy,
  Edit,
  Trash2,
  FileText,
  Upload,
  Download,
  Building2,
  Navigation,
  UserCheck,
  Flag,
  Loader2,
  Euro,
  Car,
  Star,
  AlertTriangle,
  RefreshCw,
  Save,
  ChevronDown,
  ChevronUp,
  Plane,
  Camera
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';


export default function PastRides() {
  const { user, token } = useAuth();
  const canEditPastRides = user?.section_permissions?.past_rides_edit !== false;
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [rides, setRides] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: 50,
    total: 0,
    totalPages: 0
  });
  
  // Month/Year navigation - initialize from URL params if present
  const [selectedMonth, setSelectedMonth] = useState(() => {
    const monthParam = searchParams.get('month');
    return monthParam ? parseInt(monthParam) : new Date().getMonth() + 1;
  });
  const [selectedYear, setSelectedYear] = useState(() => {
    const yearParam = searchParams.get('year');
    return yearParam ? parseInt(yearParam) : new Date().getFullYear();
  });
  
  // Filters - Same as RidesManagement section - initialize from URL params if present
  const [searchTerm, setSearchTerm] = useState(() => searchParams.get('searchTerm') || '');
  const [filterStatus, setFilterStatus] = useState(() => searchParams.get('filterStatus') || 'all');
  const [filterDriver, setFilterDriver] = useState(() => searchParams.get('filterDriver') || 'all');
  const [filterPartner, setFilterPartner] = useState(() => searchParams.get('filterPartner') || 'all');
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  
  // Advanced filters
  const [filterVehicle, setFilterVehicle] = useState(() => searchParams.get('filterVehicle') || 'all');
  const [filterPlate, setFilterPlate] = useState(() => searchParams.get('filterPlate') || 'all');
  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [filterDateTo, setFilterDateTo] = useState('');
  const [filterPriceMin, setFilterPriceMin] = useState('');
  const [filterPriceMax, setFilterPriceMax] = useState('');
  const [includeCancelled, setIncludeCancelled] = useState(false);
  const [filterEmptyField, setFilterEmptyField] = useState(() => searchParams.get('filterEmptyField') || 'none');
  
  // Restore page from URL params
  useEffect(() => {
    const pageParam = searchParams.get('page');
    if (pageParam) {
      setPagination(prev => ({ ...prev, page: parseInt(pageParam) }));
    }
    // Clear the URL params after reading them
    if (searchParams.toString()) {
      setSearchParams({});
    }
  }, []);
  
  // Data for dropdowns
  const [drivers, setDrivers] = useState([]);
  const [uniquePartners, setUniquePartners] = useState([]);
  const [uniqueVehicles, setUniqueVehicles] = useState([]);
  const [uniquePlates, setUniquePlates] = useState([]);
  
  // View ride details
  const [selectedRide, setSelectedRide] = useState(null);

  // List endpoints strip flight_delay_info.proof_data (performance); fetch the
  // full ride on open so the delay proof image still renders in the modal.
  const openRideDetail = async (ride) => {
    setSelectedRide(ride);
    if (ride.flight_delay_info) {
      try {
        const res = await axios.get(`${API}/rides/${ride.id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setSelectedRide(prev => (prev && prev.id === ride.id ? res.data : prev));
      } catch (e) { /* keep list version */ }
    }
  };
  
  // No-show proof photos for the currently selected ride
  const [noShowPhotos, setNoShowPhotos] = useState([]);
  const [loadingNoShowPhotos, setLoadingNoShowPhotos] = useState(false);
  
  // Delete confirmation
  const [rideToDelete, setRideToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  
  // File upload
  const [uploadingFile, setUploadingFile] = useState(false);
  const [fileUploadRideId, setFileUploadRideId] = useState(null);
  const fileInputRef = useState(null);

  // Rating state
  const [showRatingSection, setShowRatingSection] = useState(false);
  const [ratingData, setRatingData] = useState(null);
  const [loadingRating, setLoadingRating] = useState(false);
  const [savingRating, setSavingRating] = useState(false);
  const [ratingAdjustments, setRatingAdjustments] = useState({
    punctuality_cause: 'driver_fault',
    reliability_cause: 'driver_fault',
    has_service_complaint: false,
    no_pickup_by_driver: false,
    arrived_at_pickup_driver_fault: true,
    picked_up_customer_driver_fault: true,
    arrived_at_dropoff_driver_fault: true,
    too_fast_driver_fault: true,
  });

  // Flight delay state
  const [showFlightDelayForm, setShowFlightDelayForm] = useState(false);
  const [flightDelayTime, setFlightDelayTime] = useState('');
  const [flightDelayProof, setFlightDelayProof] = useState(null);
  const [flightDelayNotes, setFlightDelayNotes] = useState('');
  const [submittingFlightDelay, setSubmittingFlightDelay] = useState(false);

  // Month names for dropdown
  const months = [
    { value: 1, label: 'January' },
    { value: 2, label: 'February' },
    { value: 3, label: 'March' },
    { value: 4, label: 'April' },
    { value: 5, label: 'May' },
    { value: 6, label: 'June' },
    { value: 7, label: 'July' },
    { value: 8, label: 'August' },
    { value: 9, label: 'September' },
    { value: 10, label: 'October' },
    { value: 11, label: 'November' },
    { value: 12, label: 'December' }
  ];
  
  // Years for dropdown (from 2024 to current year + 1)
  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: currentYear - 2024 + 2 }, (_, i) => 2024 + i);
  
  // Month navigation functions
  const goToPreviousMonth = () => {
    if (selectedMonth === 1) {
      setSelectedMonth(12);
      setSelectedYear(selectedYear - 1);
    } else {
      setSelectedMonth(selectedMonth - 1);
    }
    setPagination(prev => ({ ...prev, page: 1 }));
  };
  
  const goToNextMonth = () => {
    if (selectedMonth === 12) {
      setSelectedMonth(1);
      setSelectedYear(selectedYear + 1);
    } else {
      setSelectedMonth(selectedMonth + 1);
    }
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  // Check if advanced filters have values
  const hasActiveAdvancedFilters = filterVehicle !== 'all' || filterPlate !== 'all' || filterDateFrom || filterDateTo || filterPriceMin || filterPriceMax || filterEmptyField !== 'none';

  // Clear all filters
  const clearAllFilters = () => {
    setSearchTerm('');
    setFilterStatus('all');
    setFilterDriver('all');
    setFilterPartner('all');
    setFilterVehicle('all');
    setFilterPlate('all');
    setFilterDateFrom('');
    setFilterDateTo('');
    setFilterPriceMin('');
    setFilterPriceMax('');
    setIncludeCancelled(false);
    setSelectedMonth(new Date().getMonth() + 1);
    setSelectedYear(new Date().getFullYear());
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  useEffect(() => {
    fetchDrivers();
    fetchPastRides();
    fetchFilterOptions();
  }, []);

  // Fetch filter options when month/year changes
  useEffect(() => {
    fetchFilterOptions();
  }, [selectedMonth, selectedYear]);

  // Debounce text inputs (search, price) to avoid excessive API calls
  useEffect(() => {
    const timer = setTimeout(() => {
      setPagination(prev => prev.page === 1 ? prev : { ...prev, page: 1 });
      fetchPastRides();
    }, 400);
    return () => clearTimeout(timer);
  }, [searchTerm, filterPriceMin, filterPriceMax]);

  // Instant-response filters (dropdowns, toggles, pagination)
  useEffect(() => {
    fetchPastRides();
  }, [pagination.page, filterStatus, filterDriver, filterPartner, filterVehicle, filterPlate, filterDateFrom, filterDateTo, includeCancelled, selectedMonth, selectedYear]);

  const fetchDrivers = async () => {
    try {
      const response = await axios.get(`${API}/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const driverUsers = response.data.filter(u => u.role === 'driver' || u.role === 'admin-driver');
      setDrivers(driverUsers);
    } catch (error) {
      console.error('Error fetching drivers:', error);
    }
  };

  const fetchFilterOptions = async () => {
    try {
      const clientTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const params = new URLSearchParams({
        month: selectedMonth.toString(),
        year: selectedYear.toString(),
        tz: clientTimezone
      });
      const response = await axios.get(`${API}/rides/past/filter-options?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUniquePartners(response.data.partners || []);
      setUniqueVehicles(response.data.vehicle_types || []);
      setUniquePlates(response.data.vehicle_plates || []);
    } catch (error) {
      console.error('Error fetching filter options:', error);
    }
  };

  const fetchPastRides = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: pagination.page.toString(),
        page_size: pagination.pageSize.toString(),
        month: selectedMonth.toString(),
        year: selectedYear.toString()
      });
      
      if (filterStatus && filterStatus !== 'all') {
        params.append('status', filterStatus);
      }
      if (filterDriver && filterDriver !== 'all') {
        params.append('driver_id', filterDriver);
      }
      if (filterPartner && filterPartner !== 'all') {
        params.append('partner', filterPartner);
      }
      if (filterDateFrom) {
        params.append('start_date', filterDateFrom);
      }
      if (filterDateTo) {
        params.append('end_date', filterDateTo);
      }
      if (searchTerm) {
        params.append('search', searchTerm);
      }
      if (filterVehicle && filterVehicle !== 'all') {
        params.append('vehicle_type', filterVehicle);
      }
      if (filterPlate && filterPlate !== 'all') {
        params.append('vehicle_plate', filterPlate);
      }
      if (filterPriceMin) {
        params.append('price_min', filterPriceMin);
      }
      if (filterPriceMax) {
        params.append('price_max', filterPriceMax);
      }
      if (!includeCancelled) {
        params.append('include_cancelled', 'false');
      }

      const response = await axios.get(`${API}/rides/past?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const fetchedRides = response.data.rides || [];
      setRides(fetchedRides);
      setPagination(prev => ({
        ...prev,
        total: response.data.total,
        totalPages: response.data.total_pages
      }));
    } catch (error) {
      console.error('Error fetching past rides:', error);
      toast.error('Failed to load past rides');
    } finally {
      setLoading(false);
    }
  };

  // Server handles most filtering. Apply empty field filter client-side.
  const filteredRides = filterEmptyField !== 'none' 
    ? rides.filter(ride => {
        const val = ride[filterEmptyField];
        return val === null || val === undefined || val === '' || val === 0;
      })
    : rides;

  const handleSearch = (e) => {
    e.preventDefault();
    // Search is already handled by the debounced useEffect on searchTerm
    // This just prevents the form from submitting/reloading
  };

  const goToPage = (page) => {
    if (page >= 1 && page <= pagination.totalPages) {
      setPagination(prev => ({ ...prev, page }));
    }
  };

  // Navigate to rides page with edit mode
  const handleEditRide = (ride) => {
    // Pass return info so we come back to Past Rides after editing
    const returnParams = new URLSearchParams({
      returnTo: 'past-rides',
      month: selectedMonth.toString(),
      year: selectedYear.toString()
    });
    // Add filters if they are set
    if (filterStatus && filterStatus !== 'all') returnParams.set('filterStatus', filterStatus);
    if (filterDriver && filterDriver !== 'all') returnParams.set('filterDriver', filterDriver);
    if (filterPlate && filterPlate !== 'all') returnParams.set('filterPlate', filterPlate);
    if (filterPartner && filterPartner !== 'all') returnParams.set('filterPartner', filterPartner);
    if (filterVehicle && filterVehicle !== 'all') returnParams.set('filterVehicle', filterVehicle);
    if (searchTerm) returnParams.set('searchTerm', searchTerm);
    if (pagination.page > 1) returnParams.set('page', pagination.page.toString());
    if (filterEmptyField && filterEmptyField !== 'none') returnParams.set('filterEmptyField', filterEmptyField);
    
    navigate(`/admin/rides?edit=${ride.id}&${returnParams.toString()}`);
  };

  // Duplicate ride
  const handleDuplicateRide = (ride) => {
    // Pass return info so we come back to Past Rides after duplicating
    const returnParams = new URLSearchParams({
      returnTo: 'past-rides',
      month: selectedMonth.toString(),
      year: selectedYear.toString()
    });
    if (filterEmptyField && filterEmptyField !== 'none') returnParams.set('filterEmptyField', filterEmptyField);
    navigate(`/admin/rides?duplicate=${ride.id}&${returnParams.toString()}`);
  };

  // Delete ride
  const handleDeleteRide = async () => {
    if (!rideToDelete) return;
    
    setDeleting(true);
    try {
      await axios.delete(`${API}/rides/${rideToDelete.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Ride deleted successfully');
      setRideToDelete(null);
      fetchPastRides();
    } catch (error) {
      console.error('Error deleting ride:', error);
      toast.error('Failed to delete ride');
    } finally {
      setDeleting(false);
    }
  };

  // Rating functions
  const loadRatingData = async (rideId) => {
    setLoadingRating(true);
    try {
      const response = await axios.get(`${API}/rides/${rideId}/rating`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRatingData(response.data);
    } catch (error) {
      console.error('Error loading rating:', error);
    } finally {
      setLoadingRating(false);
    }
  };

  const handleSaveRatingAdjustments = async () => {
    if (!selectedRide?.id) return;
    setSavingRating(true);
    try {
      const response = await axios.put(`${API}/rides/${selectedRide.id}/rating`, ratingAdjustments, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRatingData(response.data.rating);
      toast.success('Rating updated successfully');
      fetchPastRides();
    } catch (error) {
      console.error('Error saving rating:', error);
      toast.error('Error saving rating');
    } finally {
      setSavingRating(false);
    }
  };

  const handleRecalculateRating = async () => {
    if (!selectedRide?.id) return;
    setSavingRating(true);
    try {
      const response = await axios.post(`${API}/rides/${selectedRide.id}/recalculate-rating`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRatingData(response.data.rating);
      toast.success('Rating recalculated');
    } catch (error) {
      console.error('Error recalculating rating:', error);
      toast.error('Error recalculating');
    } finally {
      setSavingRating(false);
    }
  };

  // Flight delay handlers
  const handleFlightDelayProofSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File troppo grande. Massimo 10MB.');
      return;
    }
    const reader = new FileReader();
    reader.onloadend = () => {
      setFlightDelayProof({ data: reader.result, filename: file.name, preview: reader.result });
    };
    reader.readAsDataURL(file);
    e.target.value = '';
  };

  const handleSubmitFlightDelay = async () => {
    if (!selectedRide?.id || !flightDelayTime) return;
    setSubmittingFlightDelay(true);
    try {
      const rideDate = selectedRide.date_time ? new Date(selectedRide.date_time) : new Date();
      const [hours, minutes] = flightDelayTime.split(':');
      const newArrival = new Date(rideDate);
      newArrival.setHours(parseInt(hours), parseInt(minutes), 0, 0);
      if (newArrival < rideDate) {
        newArrival.setDate(newArrival.getDate() + 1);
      }
      const payload = {
        new_arrival_time: newArrival.toISOString(),
        notes: flightDelayNotes || null,
      };
      if (flightDelayProof) {
        payload.proof_data = flightDelayProof.data;
        payload.proof_filename = flightDelayProof.filename;
      }
      const response = await axios.post(`${API}/rides/${selectedRide.id}/flight-delay`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Flight delay reported successfully');
      setShowFlightDelayForm(false);
      setFlightDelayTime('');
      setFlightDelayProof(null);
      setFlightDelayNotes('');
      // Update selectedRide with the response
      if (response.data.ride) {
        setSelectedRide(response.data.ride);
      }
      fetchPastRides();
      if (selectedRide.status === 'completed' || selectedRide.status === 'no_show' || selectedRide.status === 'driver_no_show') {
        loadRatingData(selectedRide.id);
      }
    } catch (error) {
      console.error('Error reporting flight delay:', error);
      toast.error(error.response?.data?.detail || 'Error reporting delay');
    } finally {
      setSubmittingFlightDelay(false);
    }
  };

  const handleRemoveFlightDelay = async () => {
    if (!selectedRide?.id) return;
    setSubmittingFlightDelay(true);
    try {
      await axios.delete(`${API}/rides/${selectedRide.id}/flight-delay`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Flight delay report removed');
      // Refresh ride data
      const response = await axios.get(`${API}/rides/${selectedRide.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedRide(response.data);
      fetchPastRides();
      if (selectedRide.status === 'completed' || selectedRide.status === 'no_show' || selectedRide.status === 'driver_no_show') {
        loadRatingData(selectedRide.id);
      }
    } catch (error) {
      console.error('Error removing flight delay:', error);
      toast.error(error.response?.data?.detail || 'Error removing report');
    } finally {
      setSubmittingFlightDelay(false);
    }
  };

  // Initialize rating when ride is selected
  useEffect(() => {
    if (selectedRide && (selectedRide.status === 'completed' || selectedRide.status === 'no_show' || selectedRide.status === 'driver_no_show')) {
      loadRatingData(selectedRide.id);
      setRatingAdjustments({
        punctuality_cause: selectedRide.punctuality_cause || 'driver_fault',
        reliability_cause: selectedRide.reliability_cause || 'driver_fault',
        has_service_complaint: selectedRide.has_service_complaint || false,
        no_pickup_by_driver: selectedRide.no_pickup_by_driver || false,
        arrived_at_pickup_driver_fault: selectedRide.arrived_at_pickup_driver_fault !== false,
        picked_up_customer_driver_fault: selectedRide.picked_up_customer_driver_fault !== false,
        arrived_at_dropoff_driver_fault: selectedRide.arrived_at_dropoff_driver_fault !== false,
        too_fast_driver_fault: selectedRide.too_fast_driver_fault !== false,
      });
      setShowRatingSection(false);
    } else {
      setRatingData(null);
      setShowRatingSection(false);
    }
    // Reset flight delay form
    setShowFlightDelayForm(false);
    setFlightDelayTime('');
    setFlightDelayProof(null);
    setFlightDelayNotes('');
  }, [selectedRide]);

  // Load no-show proof photos when a no_show ride is selected
  useEffect(() => {
    if (selectedRide && selectedRide.status === 'no_show') {
      const loadPhotos = async () => {
        setLoadingNoShowPhotos(true);
        try {
          const res = await axios.get(`${API}/rides/${selectedRide.id}/no-show-photos`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setNoShowPhotos(Array.isArray(res.data) ? res.data : []);
        } catch (err) {
          console.error('Error loading no-show photos:', err);
          setNoShowPhotos([]);
        } finally {
          setLoadingNoShowPhotos(false);
        }
      };
      loadPhotos();
    } else {
      setNoShowPhotos([]);
    }
  }, [selectedRide?.id, selectedRide?.status, token]);

  // File upload handler
  const handleFileUpload = async (e, rideId) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setUploadingFile(true);
    setFileUploadRideId(rideId);
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      await axios.post(`${API}/rides/${rideId}/vouchers`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      toast.success('File uploaded successfully');
      fetchPastRides();
      
      // Update selected ride if viewing details
      if (selectedRide?.id === rideId) {
        const response = await axios.get(`${API}/rides/${rideId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setSelectedRide(response.data);
      }
    } catch (error) {
      console.error('Error uploading file:', error);
      toast.error(error.response?.data?.detail || 'Failed to upload file');
    } finally {
      setUploadingFile(false);
      setFileUploadRideId(null);
      e.target.value = ''; // Reset file input
    }
  };

  // Download voucher
  const handleDownloadVoucher = async (rideId, voucher) => {
    try {
      const response = await axios.get(`${API}/rides/${rideId}/vouchers/${voucher.id}/download`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', voucher.filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading file:', error);
      toast.error('Failed to download file');
    }
  };

  // Download all no-show proof photos as ZIP
  const handleDownloadNoShowZip = async (rideId) => {
    try {
      const response = await axios.get(`${API}/rides/${rideId}/no-show-photos/download-zip`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      const blob = new Blob([response.data], { type: 'application/zip' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const cd = response.headers['content-disposition'] || '';
      const m = cd.match(/filename="?([^";]+)"?/);
      link.download = m ? m[1] : `NoShow_Proofs_${rideId}.zip`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Proof photos downloaded as ZIP');
    } catch (err) {
      console.error('Download ZIP error:', err);
      toast.error(err.response?.data?.detail || 'Failed to download photos');
    }
  };

  // Download a single no-show proof photo (uses base64 data already loaded)
  const handleDownloadSingleNoShowPhoto = (photo, idx) => {
    if (!photo?.data) {
      toast.error('Photo not available');
      return;
    }
    const link = document.createElement('a');
    link.href = photo.data;
    link.download = photo.filename || `no_show_proof_${idx + 1}.jpg`;
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  // Get driver status text
  const getDriverStatus = (ride) => {
    if (!ride.assigned_driver_id) return null;
    
    if (ride.arrived_at_dropoff) {
      return { text: 'Dropped off', icon: Flag, color: 'text-green-600' };
    }
    if (ride.picked_up_customer) {
      return { text: 'Picked up customer', icon: UserCheck, color: 'text-blue-600' };
    }
    if (ride.arrived_at_pickup) {
      return { text: 'Arrived at pickup', icon: Navigation, color: 'text-amber-600' };
    }
    return { text: 'On the way', icon: Clock, color: 'text-slate-500' };
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const formatTime = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-GB', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      completed: { label: 'Completed', className: 'bg-green-100 text-green-800' },
      no_show: { label: 'No Show', className: 'bg-red-100 text-red-800' },
      active: { label: 'Active', className: 'bg-blue-100 text-blue-800' },
      in_progress: { label: 'In Progress', className: 'bg-yellow-100 text-yellow-800' },
      cancelled: { label: 'Cancelled', className: 'bg-gray-100 text-gray-800' },
      paid_cancelled: { label: 'Paid Cancelled', className: 'bg-orange-100 text-orange-800' },
      free_cancelled: { label: 'Free Cancelled', className: 'bg-gray-100 text-gray-800' }
    };
    const config = statusConfig[status] || { label: status, className: 'bg-gray-100 text-gray-800' };
    return <Badge className={config.className}>{config.label}</Badge>;
  };

  return (
    <div className="p-4 lg:p-8 pb-20 lg:pb-8">
      {/* Header */}
      <div className="mb-6 lg:mb-8">
        <div className="flex items-center gap-3 mb-2">
          <History className="h-6 w-6 lg:h-8 lg:w-8 text-slate-700" />
          <h1 className="text-2xl lg:text-3xl font-bold text-slate-900" data-testid="past-rides-title">Past Rides</h1>
        </div>
        <p className="text-sm lg:text-base text-slate-600">View completed and historical rides with pagination</p>
      </div>

      {/* Month/Year Navigation */}
      <div className="bg-slate-100 rounded-lg p-4 mb-6">
        <div className="flex items-center justify-center gap-2">
          <Button
            variant="outline"
            size="icon"
            onClick={goToPreviousMonth}
            className="h-12 w-12 bg-white rounded-lg border-slate-200"
            data-testid="prev-month-btn"
          >
            <ChevronLeft className="h-5 w-5 text-slate-600" />
          </Button>
          
          <Select 
            value={selectedMonth.toString()} 
            onValueChange={(v) => { setSelectedMonth(parseInt(v)); setPagination(p => ({...p, page: 1})); }}
          >
            <SelectTrigger className="w-[140px] h-12 bg-white rounded-lg border-slate-200" data-testid="month-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {months.map(m => (
                <SelectItem key={m.value} value={m.value.toString()}>{m.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          
          <Select 
            value={selectedYear.toString()} 
            onValueChange={(v) => { setSelectedYear(parseInt(v)); setPagination(p => ({...p, page: 1})); }}
          >
            <SelectTrigger className="w-[100px] h-12 bg-white rounded-lg border-slate-200" data-testid="year-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {years.map(y => (
                <SelectItem key={y} value={y.toString()}>{y}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          
          <Button
            variant="outline"
            size="icon"
            onClick={goToNextMonth}
            className="h-12 w-12 bg-white rounded-lg border-slate-200"
            data-testid="next-month-btn"
          >
            <ChevronRight className="h-5 w-5 text-slate-600" />
          </Button>
        </div>
      </div>

      {/* Filters Section - Same as RidesManagement */}
      <div className="bg-white border border-slate-200 rounded-sm shadow-sm mb-6 p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <Filter className="h-5 w-5 text-slate-500" />
            <h3 className="font-semibold text-slate-900">Filters</h3>
            {(filterStatus !== 'all' || filterDriver !== 'all' || searchTerm || hasActiveAdvancedFilters) && (
              <span className="text-xs bg-slate-100 text-slate-600 px-2 py-1 rounded-full">
                {filteredRides.length} of {pagination.total} rides
              </span>
            )}
          </div>
          <div className="flex items-center gap-4">
            {/* Include Cancelled Toggle */}
            <div className="flex items-center gap-2 bg-slate-50 px-3 py-1.5 rounded-lg" data-testid="include-cancelled-toggle">
              <Switch
                id="include-cancelled-past"
                checked={includeCancelled}
                onCheckedChange={setIncludeCancelled}
              />
              <Label htmlFor="include-cancelled-past" className="text-sm text-slate-600 cursor-pointer">
                Include cancelled
              </Label>
              {includeCancelled && (
                <span className="text-xs bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded">Active</span>
              )}
            </div>
            {(filterStatus !== 'all' || filterDriver !== 'all' || searchTerm || hasActiveAdvancedFilters) && (
              <Button
                variant="ghost"
                size="sm"
                onClick={clearAllFilters}
                className="text-slate-500 hover:text-slate-700"
                data-testid="clear-filters-btn"
              >
                Clear All
              </Button>
            )}
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
              className={showAdvancedFilters ? 'bg-slate-100' : ''}
              data-testid="toggle-advanced-filters"
            >
              {showAdvancedFilters ? 'Hide Advanced' : 'Advanced Filters'}
            </Button>
          </div>
        </div>

        {/* Basic Filters Row - Same layout as RidesManagement */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <Label className="text-slate-700 text-sm mb-2 block">Search All Fields</Label>
            <form onSubmit={handleSearch}>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Customer, phone, location, flight, notes..."
                  className="pl-10 h-10 rounded-sm"
                  data-testid="past-rides-search"
                />
              </div>
            </form>
          </div>
          <div>
            <Label className="text-slate-700 text-sm mb-2 block">Status</Label>
            <Select value={filterStatus} onValueChange={(v) => { setFilterStatus(v); setPagination(p => ({...p, page: 1})); }}>
              <SelectTrigger className="h-10 rounded-sm" data-testid="filter-status">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
                <SelectItem value="no_show">No Show</SelectItem>
                <SelectItem value="paid_cancelled">Paid Cancelled</SelectItem>
                <SelectItem value="free_cancelled">Free Cancelled</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-slate-700 text-sm mb-2 block">Driver</Label>
            <Select value={filterDriver} onValueChange={(v) => { setFilterDriver(v); setPagination(p => ({...p, page: 1})); }}>
              <SelectTrigger className="h-10 rounded-sm" data-testid="filter-driver">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Drivers</SelectItem>
                {drivers.map(driver => (
                  <SelectItem key={driver.id} value={driver.id}>{driver.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-slate-700 text-sm mb-2 block">Partner</Label>
            <Select value={filterPartner} onValueChange={(v) => { setFilterPartner(v); setPagination(p => ({...p, page: 1})); }}>
              <SelectTrigger className="h-10 rounded-sm" data-testid="filter-partner">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Partners</SelectItem>
                {uniquePartners.map(partner => (
                  <SelectItem key={partner} value={partner}>{partner}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Advanced Filters - Collapsible - Same as RidesManagement */}
        {showAdvancedFilters && (
          <div className="mt-4 pt-4 border-t border-slate-200">
            <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
              <div>
                <Label className="text-slate-700 text-sm mb-2 block">Vehicle Type</Label>
                <Select value={filterVehicle} onValueChange={(v) => { setFilterVehicle(v); setPagination(p => ({...p, page: 1})); }}>
                  <SelectTrigger className="h-10 rounded-sm" data-testid="filter-vehicle">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Vehicles</SelectItem>
                    {uniqueVehicles.map((vehicle) => (
                      <SelectItem key={vehicle} value={vehicle}>
                        {vehicle}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-slate-700 text-sm mb-2 block">Number Plate</Label>
                <Select value={filterPlate} onValueChange={(v) => { setFilterPlate(v); setPagination(p => ({...p, page: 1})); }}>
                  <SelectTrigger className="h-10 rounded-sm" data-testid="filter-plate">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Plates</SelectItem>
                    {uniquePlates.map((plate) => (
                      <SelectItem key={plate} value={plate}>
                        {plate}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-slate-700 text-sm mb-2 block">Date From</Label>
                <Input
                  type="date"
                  value={filterDateFrom}
                  onChange={(e) => { setFilterDateFrom(e.target.value); setPagination(p => ({...p, page: 1})); }}
                  className="h-10 rounded-sm"
                  data-testid="filter-date-from"
                />
              </div>
              <div>
                <Label className="text-slate-700 text-sm mb-2 block">Date To</Label>
                <Input
                  type="date"
                  value={filterDateTo}
                  onChange={(e) => { setFilterDateTo(e.target.value); setPagination(p => ({...p, page: 1})); }}
                  className="h-10 rounded-sm"
                  data-testid="filter-date-to"
                />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-slate-700 text-sm mb-2 block">Min Price (EUR)</Label>
                  <Input
                    type="number"
                    value={filterPriceMin}
                    onChange={(e) => setFilterPriceMin(e.target.value)}
                    placeholder="0"
                    className="h-10 rounded-sm"
                    data-testid="filter-price-min"
                  />
                </div>
                <div>
                  <Label className="text-slate-700 text-sm mb-2 block">Max Price (EUR)</Label>
                  <Input
                    type="number"
                    value={filterPriceMax}
                    onChange={(e) => setFilterPriceMax(e.target.value)}
                    placeholder="999"
                    className="h-10 rounded-sm"
                    data-testid="filter-price-max"
                  />
                </div>
              </div>
              <div>
                <Label className="text-slate-700 text-sm mb-2 block">Empty Field</Label>
                <Select value={filterEmptyField} onValueChange={setFilterEmptyField}>
                  <SelectTrigger className="h-10 rounded-sm" data-testid="filter-empty-field">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No Filter</SelectItem>
                    <SelectItem value="booking_number">Booking Number</SelectItem>
                    <SelectItem value="customer_name">Customer Name</SelectItem>
                    <SelectItem value="customer_phone">Phone</SelectItem>
                    <SelectItem value="flight_number">Flight Number</SelectItem>
                    <SelectItem value="assigned_driver_id">Driver</SelectItem>
                    <SelectItem value="partner">Partner</SelectItem>
                    <SelectItem value="price">Price</SelectItem>
                    <SelectItem value="payment_to_partner">Partner Price</SelectItem>
                    <SelectItem value="pickup_location">Pickup Location</SelectItem>
                    <SelectItem value="dropoff_location">Dropoff Location</SelectItem>
                    <SelectItem value="vehicle_type">Vehicle Type</SelectItem>
                    <SelectItem value="notes">Notes</SelectItem>
                    <SelectItem value="payment_method">Payment Method</SelectItem>
                    <SelectItem value="number_of_people">Passengers</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Pagination Info */}
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-slate-600">
          Showing {filteredRides.length > 0 ? ((pagination.page - 1) * pagination.pageSize) + 1 : 0} - {Math.min(pagination.page * pagination.pageSize, pagination.total)} of {pagination.total} rides for {months.find(m => m.value === selectedMonth)?.label} {selectedYear}
        </p>
        <p className="text-sm text-slate-500">
          Page {pagination.page} of {pagination.totalPages || 1}
        </p>
      </div>

      {/* Rides Table */}
      <div className="bg-white rounded-xl lg:rounded-lg border border-slate-200 overflow-hidden mb-6">
        {/* Desktop Table View */}
        <div className="hidden lg:block overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-slate-200 bg-slate-50">
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase sticky left-0 z-20 bg-slate-50" style={{ minWidth: 130 }}>Customer</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase sticky z-20 bg-slate-50" style={{ left: 130, minWidth: 110 }}>Date & Time</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase sticky z-20 bg-slate-50" style={{ left: 240, minWidth: 200 }}>Route</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase sticky z-20 bg-slate-50" style={{ left: 440, minWidth: 100 }}>Driver</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase sticky z-20 bg-slate-50" style={{ left: 540, minWidth: 100, boxShadow: '4px 0 6px -2px rgba(0,0,0,0.08)' }}>Partner</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Driver Status</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Status</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Price</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Files</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="10" className="px-4 py-12 text-center text-slate-500">
                    <div className="flex items-center justify-center gap-2">
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-slate-600"></div>
                      Loading past rides...
                    </div>
                  </td>
                </tr>
              ) : filteredRides.length === 0 ? (
                <tr>
                  <td colSpan="10" className="px-4 py-12 text-center text-slate-500">
                    <History className="h-12 w-12 text-slate-300 mx-auto mb-3" />
                    <p>No past rides found</p>
                    <p className="text-sm text-slate-400 mt-1">Try adjusting your filters</p>
                  </td>
                </tr>
              ) : (
                filteredRides.map(ride => {
                  const driverStatus = getDriverStatus(ride);
                  return (
                  <tr key={ride.id} className="border-b border-slate-100 hover:bg-slate-50/80 group">
                    {/* 1. Customer (sticky) */}
                    <td className="px-4 py-3 sticky left-0 z-10 bg-white group-hover:bg-slate-50" style={{ minWidth: 130 }}>
                      <div>
                        <p className="text-sm font-medium text-slate-900">{ride.customer_name || 'N/A'}</p>
                        <p className="text-xs text-slate-500">{ride.customer_phone || ''}</p>
                      </div>
                    </td>
                    {/* 2. Date & Time (sticky) */}
                    <td className="px-4 py-3 sticky z-10 bg-white group-hover:bg-slate-50" style={{ left: 130, minWidth: 110 }}>
                      <div className="flex items-center gap-2">
                        <Calendar className="h-4 w-4 text-slate-400" />
                        <div>
                          <p className="text-sm font-medium text-slate-900">{formatDate(ride.date_time)}</p>
                          <p className="text-xs text-slate-500">{formatTime(ride.date_time)}</p>
                        </div>
                      </div>
                    </td>
                    {/* 3. Route (sticky) */}
                    <td className="px-4 py-3 sticky z-10 bg-white group-hover:bg-slate-50" style={{ left: 240, minWidth: 200 }}>
                      <div className="max-w-[200px]">
                        <p className="text-xs text-slate-600 truncate" title={ride.pickup_location}>
                          <span className="text-green-600">From:</span> {ride.pickup_location || 'N/A'}
                        </p>
                        <p className="text-xs text-slate-600 truncate" title={ride.dropoff_location}>
                          <span className="text-red-600">To:</span> {ride.dropoff_location || 'N/A'}
                        </p>
                      </div>
                    </td>
                    {/* 4. Driver (sticky) */}
                    <td className="px-4 py-3 sticky z-10 bg-white group-hover:bg-slate-50" style={{ left: 440, minWidth: 100 }}>
                      <p className="text-sm text-slate-700">{ride.assigned_driver_name || 'Unassigned'}</p>
                    </td>
                    {/* 5. Partner (sticky - last, with shadow) */}
                    <td className="px-4 py-3 sticky z-10 bg-white group-hover:bg-slate-50" style={{ left: 540, minWidth: 100, boxShadow: '4px 0 6px -2px rgba(0,0,0,0.08)' }}>
                      {ride.partner ? (
                        <div className="flex flex-col gap-1">
                          <div className="flex items-center gap-1">
                            <Building2 className="h-3 w-3 text-emerald-600" />
                            <p className="text-sm text-slate-700">{ride.partner}</p>
                          </div>
                          {!['CASH', 'POS'].includes(ride.partner) && ride.partner_id && !ride.payment_to_partner && (
                            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-red-50 text-red-700 text-[10px] font-medium border border-red-200 w-fit" data-testid="partner-price-missing-badge" title="Prezzo partner mancante">
                              <AlertTriangle className="h-2.5 w-2.5" />
                              Prezzo mancante
                            </span>
                          )}
                        </div>
                      ) : (
                        <p className="text-xs text-slate-400">Direct</p>
                      )}
                    </td>
                    {/* 6. Driver Status */}
                    <td className="px-4 py-3">
                      {driverStatus ? (
                        <div className="flex items-center gap-1">
                          <driverStatus.icon className={`h-3 w-3 ${driverStatus.color}`} />
                          <p className={`text-xs ${driverStatus.color}`}>{driverStatus.text}</p>
                        </div>
                      ) : (
                        <p className="text-xs text-slate-400">-</p>
                      )}
                    </td>
                    {/* 7. Ride Status */}
                    <td className="px-4 py-3">
                      {getStatusBadge(ride.status)}
                    </td>
                    {/* 8. Price */}
                    <td className="px-4 py-3">
                      <p className="text-sm font-semibold text-slate-900">{ride.price?.toFixed(2) || '0.00'} EUR</p>
                    </td>
                    {/* Files Column */}
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1">
                        {ride.vouchers && ride.vouchers.length > 0 ? (
                          <div className="flex items-center gap-1">
                            <Badge variant="outline" className="text-xs bg-blue-50 text-blue-700 border-blue-200">
                              <FileText className="h-3 w-3 mr-1" />
                              {ride.vouchers.length}
                            </Badge>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-6 w-6 p-0"
                              onClick={() => handleDownloadVoucher(ride.id, ride.vouchers[0])}
                              title={`Download: ${ride.vouchers[0].filename}`}
                            >
                              <Download className="h-3 w-3 text-blue-600" />
                            </Button>
                          </div>
                        ) : (
                          <span className="text-xs text-slate-400">No files</span>
                        )}
                        {/* Upload button */}
                        <label className="cursor-pointer">
                          <input
                            type="file"
                            className="hidden"
                            accept=".pdf,.jpg,.jpeg,.png,.doc,.docx,.xls,.xlsx"
                            onChange={(e) => handleFileUpload(e, ride.id)}
                            disabled={uploadingFile}
                          />
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-6 w-6 p-0"
                            asChild
                            disabled={uploadingFile && fileUploadRideId === ride.id}
                          >
                            <span>
                              {uploadingFile && fileUploadRideId === ride.id ? (
                                <Loader2 className="h-3 w-3 animate-spin" />
                              ) : (
                                <Upload className="h-3 w-3 text-slate-500 hover:text-slate-700" />
                              )}
                            </span>
                          </Button>
                        </label>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => openRideDetail(ride)}
                        data-testid={`view-ride-${ride.id}`}
                        title="View Details"
                      >
                        <Eye className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDuplicateRide(ride)}
                        data-testid={`duplicate-ride-${ride.id}`}
                        title="Duplicate Ride"
                      >
                        <Copy className="h-4 w-4" />
                      </Button>
                      {canEditPastRides && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleEditRide(ride)}
                        data-testid={`edit-ride-${ride.id}`}
                        title="Edit Ride"
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                      )}
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setRideToDelete(ride)}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        data-testid={`delete-ride-${ride.id}`}
                        title="Delete Ride"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </td>
                  </tr>
                )})
              )}
            </tbody>
          </table>
        </div>

        {/* Mobile Card View */}
        <div className="lg:hidden p-4 space-y-4">
          {loading ? (
            <div className="py-12 text-center text-slate-500">
              <div className="flex items-center justify-center gap-2">
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-slate-600"></div>
                Loading past rides...
              </div>
            </div>
          ) : filteredRides.length === 0 ? (
            <div className="py-12 text-center text-slate-500">
              <History className="h-12 w-12 text-slate-300 mx-auto mb-3" />
              <p>No past rides found</p>
              <p className="text-sm text-slate-400 mt-1">Try adjusting your filters</p>
            </div>
          ) : (
            filteredRides.map(ride => {
              const driverStatus = getDriverStatus(ride);
              return (
                <div key={ride.id} className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
                  {/* Header: Customer & Status */}
                  <div className="p-4 border-b border-slate-100">
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <h3 className="font-semibold text-slate-900 truncate">{ride.customer_name || 'N/A'}</h3>
                        <p className="text-sm text-slate-500 font-mono mt-0.5">
                          {formatDate(ride.date_time)} {formatTime(ride.date_time)}
                        </p>
                      </div>
                      {getStatusBadge(ride.status)}
                    </div>
                  </div>

                  {/* Route */}
                  <div className="p-4 bg-slate-50 space-y-2">
                    <a 
                      href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(ride.pickup_location || '')}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-start gap-2 text-sm"
                    >
                      <span className="w-2 h-2 rounded-full bg-green-500 mt-1.5 flex-shrink-0"></span>
                      <span className="text-slate-700 break-words">{ride.pickup_location || 'N/A'}</span>
                    </a>
                    <a 
                      href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(ride.dropoff_location || '')}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-start gap-2 text-sm"
                    >
                      <span className="w-2 h-2 rounded-full bg-red-500 mt-1.5 flex-shrink-0"></span>
                      <span className="text-slate-700 break-words">{ride.dropoff_location || 'N/A'}</span>
                    </a>
                  </div>

                  {/* Details Grid */}
                  <div className="p-4 grid grid-cols-2 gap-3 text-sm">
                    <div className="flex items-center gap-2">
                      <User className="h-4 w-4 text-slate-400" />
                      <span className="text-slate-600 truncate">{ride.assigned_driver_name || 'Unassigned'}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Building2 className="h-4 w-4 text-slate-400" />
                      <span className="text-slate-600 truncate">{ride.partner || 'Direct'}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Euro className="h-4 w-4 text-slate-400" />
                      <span className="font-medium text-slate-900">€{ride.price?.toFixed(2) || '0.00'}</span>
                    </div>
                    {driverStatus && (
                      <div className="flex items-center gap-2">
                        <driverStatus.icon className={`h-4 w-4 ${driverStatus.color}`} />
                        <span className={`text-sm ${driverStatus.color}`}>{driverStatus.text}</span>
                      </div>
                    )}
                  </div>

                  {/* Action Buttons */}
                  <div className="px-4 pb-4 flex items-center gap-2 border-t border-slate-100 pt-3">
                    <button
                      onClick={() => openRideDetail(ride)}
                      className="flex-1 flex items-center justify-center gap-1.5 py-2.5 text-slate-600 hover:bg-slate-100 rounded-lg transition-colors text-sm font-medium"
                    >
                      <Eye className="h-4 w-4" />
                      View
                    </button>
                    {canEditPastRides && (
                    <button
                      onClick={() => handleEditRide(ride)}
                      className="flex-1 flex items-center justify-center gap-1.5 py-2.5 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors text-sm font-medium"
                    >
                      <Edit className="h-4 w-4" />
                      Edit
                    </button>
                    )}
                    <button
                      onClick={() => handleDuplicateRide(ride)}
                      className="flex-1 flex items-center justify-center gap-1.5 py-2.5 text-purple-600 hover:bg-purple-50 rounded-lg transition-colors text-sm font-medium"
                    >
                      <Copy className="h-4 w-4" />
                      Copy
                    </button>
                    <button
                      onClick={() => setRideToDelete(ride)}
                      className="p-2.5 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* Pagination Controls */}
      {pagination.totalPages > 1 && (
        <div className="flex items-center justify-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => goToPage(1)}
            disabled={pagination.page === 1}
            data-testid="pagination-first"
          >
            <ChevronsLeft className="h-4 w-4" />
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => goToPage(pagination.page - 1)}
            disabled={pagination.page === 1}
            data-testid="pagination-prev"
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          
          {/* Page Numbers */}
          <div className="flex items-center gap-1">
            {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
              let pageNum;
              if (pagination.totalPages <= 5) {
                pageNum = i + 1;
              } else if (pagination.page <= 3) {
                pageNum = i + 1;
              } else if (pagination.page >= pagination.totalPages - 2) {
                pageNum = pagination.totalPages - 4 + i;
              } else {
                pageNum = pagination.page - 2 + i;
              }
              return (
                <Button
                  key={pageNum}
                  variant={pagination.page === pageNum ? "default" : "outline"}
                  size="sm"
                  onClick={() => goToPage(pageNum)}
                  className="min-w-[40px]"
                  data-testid={`pagination-page-${pageNum}`}
                >
                  {pageNum}
                </Button>
              );
            })}
          </div>
          
          <Button
            variant="outline"
            size="sm"
            onClick={() => goToPage(pagination.page + 1)}
            disabled={pagination.page === pagination.totalPages}
            data-testid="pagination-next"
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => goToPage(pagination.totalPages)}
            disabled={pagination.page === pagination.totalPages}
            data-testid="pagination-last"
          >
            <ChevronsRight className="h-4 w-4" />
          </Button>
        </div>
      )}

      {/* Ride Details Modal */}
      <Dialog open={!!selectedRide} onOpenChange={() => setSelectedRide(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Ride Details</DialogTitle>
          </DialogHeader>
          {selectedRide && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs text-slate-500">Date & Time</Label>
                  <p className="font-medium">{formatDate(selectedRide.date_time)} {formatTime(selectedRide.date_time)}</p>
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Status</Label>
                  <div className="mt-1">{getStatusBadge(selectedRide.status)}</div>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs text-slate-500">Customer Name</Label>
                  <p className="font-medium">{selectedRide.customer_name || 'N/A'}</p>
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Customer Phone</Label>
                  <p className="font-medium">{selectedRide.customer_phone || 'N/A'}</p>
                </div>
              </div>
              
              <div>
                <Label className="text-xs text-slate-500">Pickup Location</Label>
                <p className="font-medium text-green-700">{selectedRide.pickup_location || 'N/A'}</p>
              </div>
              
              <div>
                <Label className="text-xs text-slate-500">Dropoff Location</Label>
                <p className="font-medium text-red-700">{selectedRide.dropoff_location || 'N/A'}</p>
              </div>
              
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label className="text-xs text-slate-500">Price</Label>
                  <p className="font-bold text-lg">{selectedRide.price?.toFixed(2) || '0.00'} EUR</p>
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Commission</Label>
                  <p className="font-medium">{selectedRide.commission?.toFixed(2) || '0.00'} EUR</p>
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Passengers</Label>
                  <p className="font-medium">{selectedRide.passengers || 1}</p>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs text-slate-500">Driver</Label>
                  <p className="font-medium">{selectedRide.assigned_driver_name || 'Unassigned'}</p>
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Vehicle</Label>
                  <p className="font-medium">{selectedRide.vehicle_plate || 'Not assigned'}</p>
                </div>
              </div>
              
              {/* Driver Status */}
              {selectedRide.assigned_driver_id && (
                <div className="bg-slate-50 p-3 rounded-lg">
                  <Label className="text-xs text-slate-500 mb-2 block">Driver Status Timeline</Label>
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <Navigation className={`h-4 w-4 ${selectedRide.arrived_at_pickup ? 'text-green-600' : 'text-slate-300'}`} />
                      <span className={`text-sm ${selectedRide.arrived_at_pickup ? 'text-slate-700' : 'text-slate-400'}`}>
                        Arrived at pickup {selectedRide.arrived_at_pickup ? `- ${new Date(selectedRide.arrived_at_pickup).toLocaleTimeString('en-GB', {hour: '2-digit', minute: '2-digit'})}` : ''}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <UserCheck className={`h-4 w-4 ${selectedRide.picked_up_customer ? 'text-green-600' : 'text-slate-300'}`} />
                      <span className={`text-sm ${selectedRide.picked_up_customer ? 'text-slate-700' : 'text-slate-400'}`}>
                        Picked up customer {selectedRide.picked_up_customer ? `- ${new Date(selectedRide.picked_up_customer).toLocaleTimeString('en-GB', {hour: '2-digit', minute: '2-digit'})}` : ''}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Flag className={`h-4 w-4 ${selectedRide.arrived_at_dropoff ? 'text-green-600' : 'text-slate-300'}`} />
                      <span className={`text-sm ${selectedRide.arrived_at_dropoff ? 'text-slate-700' : 'text-slate-400'}`}>
                        Dropped off {selectedRide.arrived_at_dropoff ? `- ${new Date(selectedRide.arrived_at_dropoff).toLocaleTimeString('en-GB', {hour: '2-digit', minute: '2-digit'})}` : ''}
                      </span>
                    </div>
                  </div>
                </div>
              )}
              
              {/* Partner */}
              {(selectedRide.partner || selectedRide.partner_name) && (
                <div>
                  <Label className="text-xs text-slate-500">Partner</Label>
                  <div className="flex items-center gap-2">
                    <Building2 className="h-4 w-4 text-emerald-600" />
                    <p className="font-medium">{selectedRide.partner || selectedRide.partner_name}</p>
                  </div>
                </div>
              )}

              {/* Flight Delay Section - Airport Pickup */}
              <div className="border-t pt-4">
                <div className="flex items-center justify-between mb-3">
                  <Label className="text-xs text-slate-500 flex items-center gap-1">
                    <Plane className="h-4 w-4" />
                    Flight Delay / Airport Pickup
                  </Label>
                </div>

                {/* Show existing flight delay info */}
                {selectedRide.flight_delay_info ? (
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 space-y-2" data-testid="admin-flight-delay-info">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Plane className="h-4 w-4 text-amber-600" />
                        <span className="font-semibold text-amber-800 text-sm">Flight Delay Reported</span>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={handleRemoveFlightDelay}
                        disabled={submittingFlightDelay}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50 h-8"
                        data-testid="remove-flight-delay-btn"
                      >
                        {submittingFlightDelay ? <Loader2 className="h-3 w-3 animate-spin" /> : <Trash2 className="h-3 w-3" />}
                        <span className="ml-1 text-xs">Remove</span>
                      </Button>
                    </div>
                    <div className="text-sm space-y-1">
                      <p className="text-amber-700">
                        Nuovo arrivo: <span className="font-semibold">{new Date(selectedRide.flight_delay_info.new_arrival_time).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}</span>
                      </p>
                      {selectedRide.flight_delay_info.notes && (
                        <p className="text-amber-600 text-xs">Note: {selectedRide.flight_delay_info.notes}</p>
                      )}
                      <p className="text-amber-500 text-xs">
                        Reported by: {selectedRide.flight_delay_info.reported_by_name} ({selectedRide.flight_delay_info.reported_by_role})
                        {selectedRide.flight_delay_info.reported_at && ` - ${new Date(selectedRide.flight_delay_info.reported_at).toLocaleString('en-GB')}`}
                      </p>
                    </div>
                    {selectedRide.flight_delay_info.proof_data && (
                      <div className="mt-2">
                        <p className="text-xs text-amber-600 mb-1">Proof:</p>
                        <img 
                          src={selectedRide.flight_delay_info.proof_data} 
                          alt="Flight delay proof" 
                          className="w-full max-h-40 object-cover rounded border border-amber-200"
                        />
                      </div>
                    )}
                  </div>
                ) : (
                  <>
                    {/* Airport pickup badge */}
                    {selectedRide.is_airport_pickup && (
                      <div className="flex items-center gap-2 mb-2 text-sm text-blue-600">
                        <Plane className="h-3.5 w-3.5" />
                        <span>Airport pickup (30 min tolerance)</span>
                      </div>
                    )}

                    {!showFlightDelayForm ? (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setShowFlightDelayForm(true)}
                        className="w-full border-dashed border-blue-300 text-blue-600 hover:bg-blue-50"
                        data-testid="admin-add-flight-delay-btn"
                      >
                        <Plane className="h-4 w-4 mr-2" />
                        Report Flight Delay
                      </Button>
                    ) : (
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 space-y-3" data-testid="admin-flight-delay-form">
                        <div>
                          <label className="text-xs font-medium text-slate-700">New Arrival Time *</label>
                          <input
                            type="time"
                            value={flightDelayTime}
                            onChange={(e) => setFlightDelayTime(e.target.value)}
                            className="w-full h-10 px-3 mt-1 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                            data-testid="admin-flight-delay-time"
                          />
                        </div>
                        <div>
                          <label className="text-xs font-medium text-slate-700">Proof (optional)</label>
                          {flightDelayProof ? (
                            <div className="relative mt-1">
                              <img src={flightDelayProof.preview} alt="proof" className="w-full h-24 object-cover rounded-lg border" />
                              <button onClick={() => setFlightDelayProof(null)} className="absolute top-1 right-1 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center">
                                <X className="h-3 w-3" />
                              </button>
                            </div>
                          ) : (
                            <label className="flex items-center justify-center w-full h-16 mt-1 border-2 border-dashed border-slate-300 rounded-lg cursor-pointer bg-white hover:bg-slate-50">
                              <div className="flex items-center gap-2 text-slate-500 text-xs">
                                <Camera className="h-4 w-4" />
                                <span>Upload screenshot</span>
                              </div>
                              <input type="file" accept="image/*" onChange={handleFlightDelayProofSelect} className="hidden" />
                            </label>
                          )}
                        </div>
                        <div>
                          <label className="text-xs font-medium text-slate-700">Notes (optional)</label>
                          <textarea
                            value={flightDelayNotes}
                            onChange={(e) => setFlightDelayNotes(e.target.value)}
                            placeholder="E.g. Flight from London delayed 45 minutes..."
                            className="w-full h-16 px-3 py-2 mt-1 border border-slate-300 rounded-lg resize-none text-xs focus:outline-none focus:ring-2 focus:ring-blue-500"
                            data-testid="admin-flight-delay-notes"
                          />
                        </div>
                        <div className="flex gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => { setShowFlightDelayForm(false); setFlightDelayTime(''); setFlightDelayProof(null); setFlightDelayNotes(''); }}
                            className="flex-1 h-8"
                            disabled={submittingFlightDelay}
                          >
                            Cancel
                          </Button>
                          <Button
                            size="sm"
                            onClick={handleSubmitFlightDelay}
                            disabled={!flightDelayTime || submittingFlightDelay}
                            className="flex-1 h-8 bg-blue-500 hover:bg-blue-600 text-white"
                            data-testid="admin-submit-flight-delay-btn"
                          >
                            {submittingFlightDelay ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Plane className="h-3 w-3 mr-1" />}
                            Confirm
                          </Button>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
              
              {selectedRide.notes && (
                <div>
                  <Label className="text-xs text-slate-500">Notes</Label>
                  <p className="text-sm text-slate-600 bg-slate-50 p-2 rounded">{selectedRide.notes}</p>
                </div>
              )}
              
              {/* No Show Proof Photos Section */}
              {selectedRide.status === 'no_show' && (
                <div className="border-t pt-4">
                  <div className="flex items-center justify-between mb-3">
                    <Label className="text-xs text-slate-500 flex items-center gap-1">
                      <Camera className="h-4 w-4 text-red-600" />
                      No Show Proof Photos {!loadingNoShowPhotos && `(${noShowPhotos.length})`}
                    </Label>
                    {!loadingNoShowPhotos && noShowPhotos.length > 0 && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleDownloadNoShowZip(selectedRide.id)}
                        className="text-xs h-8 border-red-300 text-red-700 hover:bg-red-100"
                        data-testid="past-rides-download-noshow-zip"
                      >
                        <Download className="h-3 w-3 mr-1" />
                        Download ZIP ({noShowPhotos.length})
                      </Button>
                    )}
                  </div>
                  {loadingNoShowPhotos ? (
                    <div className="flex justify-center py-4">
                      <Loader2 className="h-5 w-5 animate-spin text-red-600" />
                    </div>
                  ) : noShowPhotos.length > 0 ? (
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 bg-red-50 p-3 rounded-lg" data-testid="noshow-photos-grid">
                      {noShowPhotos.map((photo, idx) => (
                        <div key={photo.id || idx} className="relative group">
                          {photo.data ? (
                            <img
                              src={photo.data}
                              alt={`Proof ${idx + 1}`}
                              className="w-full h-28 object-cover rounded border border-red-200 cursor-pointer hover:opacity-90"
                              onClick={() => window.open(photo.data, '_blank')}
                              data-testid={`noshow-photo-${idx}`}
                            />
                          ) : (
                            <div className="w-full h-28 bg-slate-100 border border-red-200 rounded flex items-center justify-center text-xs text-slate-500">
                              {photo.missing ? 'Photo missing' : 'No data'}
                            </div>
                          )}
                          <div className="absolute top-1 left-1 bg-black/50 text-white text-[10px] px-1.5 py-0.5 rounded">
                            {idx + 1}/{noShowPhotos.length}
                          </div>
                          {photo.data && (
                            <button
                              onClick={(e) => { e.stopPropagation(); handleDownloadSingleNoShowPhoto(photo, idx); }}
                              className="absolute bottom-1 right-1 w-7 h-7 bg-white/95 hover:bg-white rounded-full flex items-center justify-center shadow"
                              title="Download this photo"
                              data-testid={`past-rides-download-noshow-${idx}`}
                            >
                              <Download className="h-3 w-3 text-slate-700" />
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-slate-500 text-center py-3 bg-slate-50 rounded">
                      No proof photos uploaded for this ride.
                    </p>
                  )}
                  {selectedRide.no_show_notes && (
                    <div className="mt-2 bg-red-50 p-2 rounded">
                      <p className="text-xs text-red-700 font-medium">Driver note:</p>
                      <p className="text-sm text-slate-700">{selectedRide.no_show_notes}</p>
                    </div>
                  )}
                </div>
              )}

              {/* Files/Vouchers Section */}
              <div className="border-t pt-4">
                <div className="flex items-center justify-between mb-3">
                  <Label className="text-xs text-slate-500 flex items-center gap-1">
                    <FileText className="h-4 w-4" />
                    Files & Documents
                  </Label>
                  <label className="cursor-pointer">
                    <input
                      type="file"
                      className="hidden"
                      accept=".pdf,.jpg,.jpeg,.png,.doc,.docx,.xls,.xlsx"
                      onChange={(e) => handleFileUpload(e, selectedRide.id)}
                      disabled={uploadingFile}
                    />
                    <Button
                      variant="outline"
                      size="sm"
                      asChild
                      disabled={uploadingFile}
                    >
                      <span>
                        {uploadingFile && fileUploadRideId === selectedRide.id ? (
                          <>
                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                            Uploading...
                          </>
                        ) : (
                          <>
                            <Upload className="h-4 w-4 mr-2" />
                            Add File
                          </>
                        )}
                      </span>
                    </Button>
                  </label>
                </div>
                
                {selectedRide.vouchers && selectedRide.vouchers.length > 0 ? (
                  <div className="space-y-2">
                    {selectedRide.vouchers.map((voucher, idx) => (
                      <div key={voucher.id || idx} className="flex items-center justify-between bg-slate-50 p-2 rounded">
                        <div className="flex items-center gap-2 min-w-0 flex-1">
                          <FileText className="h-4 w-4 text-blue-600 flex-shrink-0" />
                          <span className="text-sm text-slate-700 truncate" title={voucher.filename}>
                            {voucher.filename}
                          </span>
                          <span className="text-xs text-slate-400 flex-shrink-0">
                            {voucher.uploaded_at ? new Date(voucher.uploaded_at).toLocaleDateString('en-GB') : ''}
                          </span>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDownloadVoucher(selectedRide.id, voucher)}
                          title="Download"
                        >
                          <Download className="h-4 w-4 text-blue-600" />
                        </Button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-slate-400 text-center py-4">No files uploaded yet</p>
                )}
              </div>

              {/* Driver Rating Section - For Completed/No-Show Rides */}
              {(selectedRide.status === 'completed' || selectedRide.status === 'no_show' || selectedRide.status === 'driver_no_show') && (
                <div className="border-t pt-4 mt-4">
                  <div 
                    className="flex items-center justify-between cursor-pointer bg-amber-50 p-3 rounded-lg"
                    onClick={() => setShowRatingSection(!showRatingSection)}
                  >
                    <div className="flex items-center gap-2">
                      <Star className="h-4 w-4 text-amber-600" />
                      <span className="font-semibold text-amber-900">Ride Rating</span>
                      {ratingData?.total_score !== undefined && (
                        <span className={`ml-2 px-2 py-0.5 rounded-full text-sm font-bold ${
                          ratingData.total_score >= 80 ? 'bg-emerald-100 text-emerald-800' :
                          ratingData.total_score >= 60 ? 'bg-amber-100 text-amber-800' :
                          'bg-red-100 text-red-800'
                        }`}>
                          {ratingData.total_score.toFixed(1)} / 100
                        </span>
                      )}
                    </div>
                    <button className="text-amber-700 hover:text-amber-900">
                      {showRatingSection ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
                    </button>
                  </div>

                  {showRatingSection && (
                    <div className="mt-3 space-y-4">
                      {loadingRating ? (
                        <div className="flex justify-center py-4">
                          <Loader2 className="h-6 w-6 animate-spin text-amber-600" />
                        </div>
                      ) : (
                        <>
                          {/* Rating Breakdown Display */}
                          {ratingData && (
                            <div className="bg-slate-50 rounded-lg p-3">
                              <p className="text-sm font-semibold text-slate-700 mb-2">Rating Breakdown:</p>
                              <div className="grid grid-cols-2 gap-2 text-sm">
                                <div className="bg-white p-2 rounded border">
                                  <p className="text-xs text-slate-500">Ride Management (35%)</p>
                                  <p className="font-bold">{ratingData.ride_management?.score || 0}</p>
                                  {ratingData.ride_management?.errors?.length > 0 && (
                                    <p className="text-xs text-red-600 mt-1">{ratingData.ride_management.errors.join(', ')}</p>
                                  )}
                                  {ratingData.ride_management?.admin_performed_actions?.length > 0 && (
                                    <div className="mt-1 space-y-0.5">
                                      {ratingData.ride_management.admin_performed_actions.map((a, i) => (
                                        <p key={i} className="text-[10px] text-orange-600 font-medium">
                                          ⚠ {a.action}: da {a.by}
                                        </p>
                                      ))}
                                    </div>
                                  )}
                                </div>
                                <div className="bg-white p-2 rounded border">
                                  <p className="text-xs text-slate-500">Reliability (25%)</p>
                                  <p className="font-bold">{ratingData.reliability?.score || 0}</p>
                                  {ratingData.reliability?.details && (
                                    <p className="text-xs text-slate-600 mt-1">{ratingData.reliability.details}</p>
                                  )}
                                </div>
                                <div className="bg-white p-2 rounded border">
                                  <p className="text-xs text-slate-500">Service Quality (5%)</p>
                                  <p className="font-bold">{ratingData.service_quality?.score || 0}</p>
                                  {ratingData.service_quality?.has_complaint && (
                                    <p className="text-xs text-red-600 mt-1">Complaint present</p>
                                  )}
                                </div>
                                <div className="bg-white p-2 rounded border">
                                  <p className="text-xs text-slate-500">
                                    Punctuality (35%)
                                    {ratingData.punctuality?.excluded && <span className="text-red-500"> - ESCLUSO</span>}
                                  </p>
                                  <p className="font-bold">{ratingData.punctuality?.score || 0}</p>
                                  {ratingData.punctuality?.delay_minutes > 0 && (
                                    <p className="text-xs text-slate-600 mt-1">Delay: {ratingData.punctuality.delay_minutes} min</p>
                                  )}
                                </div>
                              </div>
                            </div>
                          )}

                          {/* Admin Adjustments */}
                          <div className="bg-amber-50 rounded-lg p-3 border border-amber-200">
                            <p className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
                              <AlertTriangle className="h-4 w-4 text-amber-600" />
                              Modifiche Admin
                            </p>
                            
                            <div className="space-y-3">
                              {/* Punctuality Cause */}
                              <div>
                                <Label className="text-xs text-slate-600">Delay Cause</Label>
                                <select
                                  value={ratingAdjustments.punctuality_cause}
                                  onChange={(e) => setRatingAdjustments(prev => ({
                                    ...prev,
                                    punctuality_cause: e.target.value
                                  }))}
                                  className="w-full mt-1 border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500"
                                  data-testid="punctuality-cause-select-past"
                                >
                                  <option value="driver_fault">Driver's Fault</option>
                                  <option value="external_causes">Cause esterne</option>
                                </select>
                              </div>

                              {/* Reliability Cause */}
                              <div>
                                <Label className="text-xs text-slate-600">Reliability Issue Cause</Label>
                                <select
                                  value={ratingAdjustments.reliability_cause}
                                  onChange={(e) => setRatingAdjustments(prev => ({
                                    ...prev,
                                    reliability_cause: e.target.value
                                  }))}
                                  className="w-full mt-1 border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500"
                                  data-testid="reliability-cause-select-past"
                                >
                                  <option value="driver_fault">Driver's Fault</option>
                                  <option value="external_causes">Cause esterne</option>
                                </select>
                              </div>

                              {/* Service Complaint Toggle */}
                              <div className="flex items-center justify-between">
                                <div>
                                  <Label className="text-xs text-slate-600">Service Quality Complaint</Label>
                                  <p className="text-xs text-slate-500">-50 punti qualità</p>
                                </div>
                                <button
                                  type="button"
                                  onClick={() => setRatingAdjustments(prev => ({
                                    ...prev,
                                    has_service_complaint: !prev.has_service_complaint
                                  }))}
                                  className={`relative w-10 h-5 rounded-full transition-colors ${
                                    ratingAdjustments.has_service_complaint ? 'bg-red-500' : 'bg-slate-300'
                                  }`}
                                  data-testid="complaint-toggle-past"
                                >
                                  <span className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full transition-transform ${
                                    ratingAdjustments.has_service_complaint ? 'translate-x-5' : ''
                                  }`} />
                                </button>
                              </div>

                              {/* No Pickup Toggle */}
                              <div className="flex items-center justify-between">
                                <div>
                                  <Label className="text-xs text-slate-600">Driver No-Show</Label>
                                  <p className="text-xs text-slate-500">0 punti affidabilità</p>
                                </div>
                                <button
                                  type="button"
                                  onClick={() => setRatingAdjustments(prev => ({
                                    ...prev,
                                    no_pickup_by_driver: !prev.no_pickup_by_driver
                                  }))}
                                  className={`relative w-10 h-5 rounded-full transition-colors ${
                                    ratingAdjustments.no_pickup_by_driver ? 'bg-red-500' : 'bg-slate-300'
                                  }`}
                                  data-testid="no-pickup-toggle-past"
                                >
                                  <span className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full transition-transform ${
                                    ratingAdjustments.no_pickup_by_driver ? 'translate-x-5' : ''
                                  }`} />
                                </button>
                              </div>

                              {/* Too fast pickup→dropoff override */}
                              {ratingData?.ride_management?.too_fast && (
                                <div className="border-t border-amber-200 pt-3 mt-2 col-span-2" data-testid="too-fast-override-past">
                                  <div className="flex items-center justify-between bg-amber-50 rounded-md p-2">
                                    <div className="min-w-0 pr-2">
                                      <p className="text-xs font-medium text-slate-700">
                                        Pickup → dropoff troppo veloce ({ratingData.ride_management.too_fast.minutes} min)
                                      </p>
                                      <p className="text-[10px] text-slate-500">
                                        {ratingAdjustments.too_fast_driver_fault === false
                                          ? 'Non colpa del driver — nessuna penalità'
                                          : 'Colpa del driver — penalità −20 punti'}
                                      </p>
                                    </div>
                                    <button
                                      type="button"
                                      onClick={() => setRatingAdjustments(prev => ({
                                        ...prev,
                                        too_fast_driver_fault: !prev.too_fast_driver_fault
                                      }))}
                                      className={`relative shrink-0 w-12 h-6 rounded-full transition-colors ${
                                        ratingAdjustments.too_fast_driver_fault !== false ? 'bg-red-500' : 'bg-emerald-500'
                                      }`}
                                      data-testid="too-fast-fault-toggle-past"
                                    >
                                      <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full transition-transform ${
                                        ratingAdjustments.too_fast_driver_fault !== false ? '' : 'translate-x-6'
                                      }`} />
                                    </button>
                                  </div>
                                </div>
                              )}

                              {/* Per-action admin overrides */}
                              {ratingData?.ride_management?.admin_performed_actions?.length > 0 && (
                                <div className="border-t border-amber-200 pt-3 mt-2 space-y-2 col-span-2" data-testid="admin-action-overrides-past">
                                  <p className="text-xs font-semibold text-amber-700">
                                    Azioni premute dall'admin — colpa del driver?
                                  </p>
                                  {ratingData.ride_management.admin_performed_actions.map((a, i) => (
                                    <div key={i} className="flex items-center justify-between bg-amber-50 rounded-md p-2">
                                      <div className="min-w-0 pr-2">
                                        <p className="text-xs font-medium text-slate-700 truncate">
                                          {a.action} <span className="text-slate-400">— da {a.by}</span>
                                        </p>
                                        <p className="text-[10px] text-slate-500">
                                          {ratingAdjustments[a.fault_field] === false
                                            ? 'Non colpa del driver — nessuna penalità'
                                            : 'Colpa del driver — penalità −25 punti'}
                                        </p>
                                      </div>
                                      <button
                                        type="button"
                                        onClick={() => setRatingAdjustments(prev => ({
                                          ...prev,
                                          [a.fault_field]: !prev[a.fault_field]
                                        }))}
                                        className={`relative shrink-0 w-10 h-5 rounded-full transition-colors ${
                                          ratingAdjustments[a.fault_field] !== false ? 'bg-red-500' : 'bg-emerald-500'
                                        }`}
                                        data-testid={`admin-action-fault-past-${a.fault_field}`}
                                      >
                                        <span className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full transition-transform ${
                                          ratingAdjustments[a.fault_field] !== false ? '' : 'translate-x-5'
                                        }`} />
                                      </button>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>

                            {/* Action Buttons */}
                            <div className="flex gap-2 mt-4">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={handleRecalculateRating}
                                disabled={savingRating}
                                className="flex-1"
                                data-testid="recalculate-rating-btn-past"
                              >
                                <RefreshCw className={`h-4 w-4 mr-1 ${savingRating ? 'animate-spin' : ''}`} />
                                Ricalcola
                              </Button>
                              <Button
                                size="sm"
                                onClick={handleSaveRatingAdjustments}
                                disabled={savingRating}
                                className="flex-1 bg-amber-600 hover:bg-amber-700"
                                data-testid="save-rating-btn-past"
                              >
                                {savingRating ? (
                                  <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                                ) : (
                                  <Save className="h-4 w-4 mr-1" />
                                )}
                                Save
                              </Button>
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!rideToDelete} onOpenChange={(open) => !open && setRideToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Ride</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this ride for{' '}
              <strong>{rideToDelete?.customer_name || 'this customer'}</strong>?
              {rideToDelete?.pickup_location && (
                <span className="block mt-1 text-sm">
                  {rideToDelete.pickup_location} - {rideToDelete.dropoff_location}
                </span>
              )}
              <span className="block mt-2 text-red-600">
                This action cannot be undone.
              </span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteRide}
              disabled={deleting}
              className="bg-red-600 hover:bg-red-700"
            >
              {deleting ? 'Deleting...' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
